import React, { useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useCompactLayout } from '../hooks/useCompactLayout';
import { CalculatorModal, CalculatorType } from './CalculatorModal';
import type { HealthCalculatorInitialData, HealthCalculatorSavePayload } from './calculators/types';

interface MonetaryInputProps {
    value: number;
    onChange: (value: number) => void;
    onBlur?: () => void;
    unit: 'year' | 'month' | 'none';
    placeholder?: string;
    decimalPlaces?: number;
    calculatorType?: CalculatorType;
    disabled?: boolean;
    min?: number;
    style?: React.CSSProperties;
    textColor?: string; // Override text color (e.g., 'white' for green backgrounds)
    /** When calculatorType is 'health': initial data for the three tabs (from case.medical). */
    healthCalculatorData?: HealthCalculatorInitialData;
    /** When calculatorType is 'health': called when user applies; update case.medical (toggles + sections + monthlyMedicalCost). */
    onHealthCalculatorSave?: (amount: number, payload: HealthCalculatorSavePayload) => void;
    /** When calculatorType is 'health' and feature flag on: if false, calculator icon opens ProUpgradeModal via onProUpgrade. */
    isPro?: boolean;
    /** When calculatorType is 'health' and !isPro: called when user clicks calculator icon (show ProUpgradeModal). */
    onProUpgrade?: () => void;
}

export const MonetaryInput: React.FC<MonetaryInputProps> = ({
    value,
    onChange,
    onBlur,
    unit,
    placeholder = "0.00",
    decimalPlaces = 2,
    calculatorType = 'income',
    disabled = false,
    min = 0,
    style,
    textColor,
    healthCalculatorData,
    onHealthCalculatorSave,
    isPro,
    onProUpgrade,
}) => {
    const theme = useTheme();
    const healthInsuranceCalculatorEnabled = useFeatureFlag('HealthInsuranceCalculator');
    const [isEditing, setIsEditing] = useState(false);
    const [displayValue, setDisplayValue] = useState('');
    const [showCalculator, setShowCalculator] = useState(false);

    const handleCalculatorClick = () => {
        if (calculatorType === 'health' && healthInsuranceCalculatorEnabled && !isPro && onProUpgrade) {
            onProUpgrade();
            return;
        }
        setShowCalculator(true);
    };

    const handleCalculatorClose = () => {
        setShowCalculator(false);
    };

    const handleCalculatorSave = (amount: number, payload?: HealthCalculatorSavePayload) => {
        onChange(amount);
        if (payload && onHealthCalculatorSave) onHealthCalculatorSave(amount, payload);
        setShowCalculator(false);
    };

    const formatCurrency = (val: number) => {
        const absVal = Math.abs(val);
        const formatted = absVal.toLocaleString('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
        });
        return val < 0 ? `-${formatted}` : formatted;
    };

    const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        let inputValue = input.value;

        // Allow negative numbers if min < 0
        const allowNegative = min < 0;

        // Only allow numbers, decimal point, and minus sign (if allowed)
        if (allowNegative) {
            // Allow minus only at the start
            if (inputValue.startsWith('-')) {
                inputValue = '-' + inputValue.substring(1).replace(/[^0-9.]/g, '');
            } else {
                inputValue = inputValue.replace(/[^0-9.]/g, '');
            }
        } else {
            // Only allow numbers and decimal point
            inputValue = inputValue.replace(/[^0-9.]/g, '');
        }

        // Only allow one decimal point
        const parts = inputValue.split('.');
        if (parts.length > 2) {
            inputValue = parts[0] + '.' + parts.slice(1).join('');
        }

        // Limit to specified decimal places
        const decimalIndex = inputValue.indexOf('.');
        if (decimalIndex !== -1) {
            const decimalPart = inputValue.substring(decimalIndex + 1);
            if (decimalPart.length > decimalPlaces) {
                inputValue = inputValue.substring(0, decimalIndex + decimalPlaces + 1); // Keep only specified decimal places
                input.value = inputValue; // Update the input value
            }
        }

        setDisplayValue(inputValue);
        // Remove commas and parse the numeric value
        const cleanValue = inputValue.replace(/,/g, '');
        const numericValue = cleanValue === '' || cleanValue === '-' ? 0 : parseFloat(cleanValue) || 0;
        // Round to the exact number of decimal places specified
        const roundedValue = Math.round(numericValue * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        onChange(roundedValue);
    };

    const handleNumericFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsEditing(true);
        const input = e.target;
        // When focusing, show the raw number value for editing
        const rawValue = value.toString();
        setDisplayValue(rawValue);
        input.value = rawValue;
        // Select all text when focusing for easy editing
        setTimeout(() => input.select(), 0);
    };

    const handleNumericBlur = () => {
        setIsEditing(false);
        setDisplayValue('');
        // Ensure the final value is rounded to the exact decimal places
        const roundedValue = Math.round(value * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        if (roundedValue !== value) {
            onChange(roundedValue);
        }
        // Call the external onBlur handler if provided
        if (onBlur) {
            onBlur();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Handle Enter key for navigation
        if (e.key === 'Enter') {
            e.preventDefault();
            // Move focus to the next focusable element (like Tab key)
            const currentElement = e.currentTarget;
            const focusableElements = document.querySelectorAll(
                'input, button, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const currentIndex = Array.from(focusableElements).indexOf(currentElement);
            const nextElement = focusableElements[currentIndex + 1] as HTMLElement;

            if (nextElement) {
                nextElement.focus();
            }
            return;
        }

        // Allow negative numbers if min < 0
        const allowNegative = min < 0;

        // Allow minus key only if negative numbers are allowed and cursor is at the start
        if (allowNegative && e.key === '-') {
            const input = e.currentTarget;
            const cursorPosition = input.selectionStart || 0;
            // Only allow minus at the start of the input
            if (cursorPosition === 0 && !input.value.startsWith('-')) {
                return; // Allow the minus key
            } else {
                e.preventDefault();
                return;
            }
        }

        // Allow: backspace, delete, tab, escape, decimal point, arrow keys, and numbers
        // Also allow: Ctrl/Cmd + A, C, V, X (for copy/paste)
        if (
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Escape' ||
            e.key === '.' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            (e.key >= '0' && e.key <= '9') ||
            ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'c' || e.key === 'v' || e.key === 'x'))
        ) {
            // Allow the key
            return;
        }

        // Prevent all other keys (letters, special characters, etc.)
        e.preventDefault();
    };

    const unitText = unit === 'year' ? '/ yr' : unit === 'month' ? '/ mo' : '';

    const showCalculatorButton = calculatorType !== 'none' && !disabled && (calculatorType !== 'health' || healthInsuranceCalculatorEnabled);

    // Below this the field cannot show a six-figure amount ("999,999.00") alongside the calculator
    // button and the unit chip, so those two drop onto a second line and the value keeps the first.
    // Must stay below 180px: the Budget expense rows give the amount a deliberate 180px column at
    // desktop, and collapsing those was a real regression. Anything narrower than this genuinely
    // cannot show "$999,999.00" beside the calculator button and the unit chip.
    const rowRef = useRef<HTMLDivElement>(null);
    const isCompact = useCompactLayout(rowRef, 175, 190);

    // When disabled, show a display box matching the enabled layout
    if (disabled) {
        const formattedValue = formatCurrency(value);

        return (
            <div ref={rowRef} className="rw-money" style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.sm,
                backgroundColor: theme.colors.surfaceSecondary,
                padding: '2px',
                width: 'fit-content',
                minWidth: '150px',
                ...(isCompact ? { flexWrap: 'wrap' as const, rowGap: '2px' } : {}),
            }}>
                <span style={{
                    order: isCompact ? 0 : undefined,
                    fontSize: theme.typography.fontSize.sm,
                    padding: `0 ${theme.spacing.xs}`,
                    color: value < 0 ? '#dc2626' : theme.colors.text.primary,
                }}>
                    $
                </span>
                <span style={{
                    order: isCompact ? 1 : undefined,
                    fontSize: theme.typography.fontSize.sm,
                    padding: theme.spacing.sm,
                    color: value < 0 ? '#dc2626' : theme.colors.text.primary,
                    minWidth: isCompact ? 0 : '110px',
                    // basis 0, not auto: with `auto` the amount's intrinsic width claimed a whole
                    // line and pushed the "$" onto its own row above it.
                    flex: isCompact ? '1 1 0' : undefined,
                    paddingLeft: isCompact ? 0 : undefined,
                    // An amount must never break across lines -- "3,333.33" was rendering as
                    // "3,3 / 33. / 33" once the surrounding grid allowed words to break.
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                }}>
                    {formattedValue}
                </span>
                {isCompact && <div style={{ order: 2, flexBasis: '100%', height: 0 }} aria-hidden />}
                {unitText && (
                    <div style={{
                        order: isCompact ? 3 : undefined,
                        backgroundColor: theme.colors.surfaceSecondary,
                        paddingTop: theme.spacing.xs,
                        paddingRight: theme.spacing.xs,
                        paddingLeft: theme.spacing.xs,
                        paddingBottom: theme.spacing.xs,
                        borderRadius: '2px',
                        border: `1px solid ${theme.colors.border.default}`,
                        marginRight: theme.spacing.xs,
                        marginLeft: theme.spacing.xs
                    }}>
                        <span style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: value < 0 ? '#dc2626' : theme.colors.text.primary,
                            paddingLeft: theme.spacing.sm,
                            paddingRight: theme.spacing.sm,
                            whiteSpace: 'nowrap',
                        }}>
                            {unitText}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div ref={rowRef} className="rw-money" style={{
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.sm,
                backgroundColor: theme.colors.surface,
                padding: '2px',
                width: style?.width || 'fit-content',
                minWidth: style?.width ? '100%' : '150px',
                ...style,
                ...(isCompact ? { flexWrap: 'wrap' as const, rowGap: '2px' } : {}),
            }}>
                {showCalculatorButton && (
                    <>
                        {calculatorType === 'health' && healthInsuranceCalculatorEnabled && (
                            <button
                                type="button"
                                onClick={handleCalculatorClick}
                                style={{
                                    order: isCompact ? 3 : undefined,
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: theme.spacing.sm,
                                    borderRadius: theme.borderRadius.sm,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <i className="fas fa-gem" style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.primary }}></i>
                            </button>
                        )}
                        <button
                            onClick={handleCalculatorClick}
                            style={{
                                order: isCompact ? 3 : undefined,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: theme.spacing.sm,
                                borderRadius: theme.borderRadius.sm,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <i className="fas fa-calculator" style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.primary }}></i>
                        </button>
                    </>
                )}
                <span style={{
                    order: isCompact ? 0 : undefined,
                    fontSize: style?.fontSize || theme.typography.fontSize.sm,
                    padding: `0 ${theme.spacing.xs}`,
                    color: textColor || (value < 0 ? '#dc2626' : theme.colors.text.primary)
                }}>$</span>
                <input
                    type="text"
                    value={isEditing ? displayValue : formatCurrency(value)}
                    onChange={handleNumericChange}
                    onFocus={handleNumericFocus}
                    onBlur={handleNumericBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    step={decimalPlaces === 0 ? "1" : `0.${"0".repeat(decimalPlaces - 1)}1`}
                    pattern={decimalPlaces === 0 ? "[0-9]*" : `[0-9]*\\.?[0-9]{0,${decimalPlaces}}`}
                    style={{
                        order: isCompact ? 1 : undefined,
                        flex: 1,
                        minWidth: 0,
                        padding: theme.spacing.sm,
                        border: 'none',
                        backgroundColor: 'transparent',
                        fontSize: style?.fontSize || theme.typography.fontSize.sm,
                        textAlign: style?.textAlign || 'left',
                        outline: 'none',
                        cursor: 'text',
                        color: textColor || (value < 0 ? '#dc2626' : theme.colors.text.primary),
                    }}
                />
                {isCompact && <div style={{ order: 2, flexBasis: '100%', height: 0 }} aria-hidden />}
                {unitText && (
                    <div style={{
                        order: isCompact ? 4 : undefined,
                        backgroundColor: theme.colors.surfaceSecondary,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingLeft: 0,
                        paddingBottom: 0,
                        borderRadius: '2px',
                        border: `1px solid ${theme.colors.border.default}`,
                        marginRight: 0,
                        marginLeft: 0,
                    }}>
                        <span style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: value < 0 ? '#dc2626' : theme.colors.text.primary,
                            paddingLeft: theme.spacing.sm,
                            paddingRight: theme.spacing.sm,
                            whiteSpace: 'nowrap',
                        }}>
                            {unitText}
                        </span>
                    </div>
                )}
            </div>

            {/* Calculator Modal */}
            {showCalculatorButton && (
                <CalculatorModal
                    isOpen={showCalculator}
                    onClose={handleCalculatorClose}
                    onSave={handleCalculatorSave}
                    currentValue={value.toString()}
                    calculatorType={calculatorType}
                    unit={unit}
                    decimalPlaces={decimalPlaces}
                    healthInitialData={calculatorType === 'health' ? healthCalculatorData : undefined}
                    onHealthCalculatorSave={calculatorType === 'health' && onHealthCalculatorSave ? (amt, payload) => handleCalculatorSave(amt, payload) : undefined}
                />
            )}
        </div>
    );
};
